import * as React from 'react'
import { Box } from '@dtx-company/shared-components/src/components/atoms/Box/index'
import { ColorSelector } from '../../landing-page/color-selector'
import { DesignOptions } from '../../landing-page/design-options'
import { Flowcode } from '@dtx-company/flowcode-generator-browser/src'
import {
  FlowcodeColorOptionType,
  FlowcodeTemplateType
} from '@app/common/src/types/createFlowcode.types'
import { FlowcodeLimitUpsellModal } from '@app/upsell-modals/src/FlowcodeLimitUpsellModal/FlowcodeLimitUpsellModal'
import { HorizontalSpacer } from '@dtx-company/shared-components/src/components/atoms/Spacer/index'
import { LOGGED_OUT_GENERATOR_COLORS } from './constants'
import { LoggedInDownloadStep } from './LoggedInDownloadStep'
import { LoggedOutDownloadStep } from './LoggedOutDownloadStep'
import { REDUCE_COLORS_FC_DESKTOP_GENERATOR } from '@app/code/src/constants/optimizely'
import { Routes } from '@dtx-company/true-common/src/constants/routes'
import { SIMPLIFIED_DESKTOP_SCAN_DESTINATIONS } from '../CreateFlowcodeForm/CreateFlowcodeFormMobile/ScanDestination/ScanDestination.contants'
import { ShapeSelector } from '../CreateFlowcodeForm/CreateFlowcodeFormDesktop/ShapeSelector'
import { SimplifiedScanDestinationV1 } from '../CreateFlowcodeForm/CreateFlowcodeFormMobile/ScanDestination/ScanDestination'
import { StepContainer } from '../HomepageVariants/HomepageVariants.subcomponents'
import { UPDATED_FLOWCODE_COLOR_OPTIONS } from '@app/code/src/constants/flowcode'
import { UpsellModalProps } from '@app/upsell-modals/src/UpsellModal/UpsellModalProps'
import { loadImage } from '@app/code/src/utils/flowcode'
import { sendErrorNotification } from '@dtx-company/inter-app/src/utils/notifications'
import { theme } from '@dtx-company/shared-components/src'
import { useAddCenterImageState } from '../CreateFlowcodeForm/AddCenterImage/AddCenterImage.hooks'
import { useAuthState } from '@dtx-company/inter-app/src/hooks/useAuthState'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useCreateAndDownloadCode } from '../CreateFlowcodeForm/CreateFlowcodeFormDesktop/CreateFlowcodeFormDesktop.hooks'
import {
  useCreateFlowcodeForm,
  useCreateFlowcodeStateActions,
  useFlowcodeTemplates
} from '../CreateFlowcodeForm/CreateFlowcodeForm.hooks'
import { useCreateFlowcodeState } from '../../../hooks/useCreateFlowcodeState'
import { useFlowExperiment } from '@dtx-company/inter-app/src/hooks/useFlowExperiment/useFlowExperiment'
import { useHandleCenterImageChange } from '../CreateFlowcodeForm/AddCenterImage/UploadCenterImageInput/UploadCenterImageInput.hooks'
import { useIsAccountPaused } from '@dtx-company/inter-app/src/hooks/useIsAccountPaused'
import { useManualDesignStep } from '../CreateFlowcodeForm/CreateFlowcodeFormDesktop/ManualDesignStep/ManualDesignStep.hooks'
import { useRedirectToPausedAccountPage } from '@dtx-company/inter-app/src/hooks/useRedirectToPausedAccountPage'
import events from '@dtx-company/inter-app/src/event-tracking/events/homepage'
import router from 'next/router'
import styled from 'styled-components'
import useTranslation from 'next-translate/useTranslation'

const InputWrapper = styled(Box)`
  border-left: 1px solid ${theme.colors.primary.black};
  margin-left: -20px;
  padding: 8px 0 24px 20px;

  ul[data-testid='flowcode-color-selector'] > li > button {
    height: 36px;
    width: 36px;
  }

  ul[data-testid='flowcode-color-selector'] div[data-testid='/icons/checkmark.svg'] {
    top: 16px;
    left: 15px;
    width: 16px;
  }
`

const VerticalDivider = styled(Box)`
  content: '';
  width: 1px;
  display: block;
`
const FLOWCODE_IMAGE_SIZE_PX = 300
const FlowcodeBox = styled(Box)`
  flexdirection: column;
  justify-content: center;
  align-items: center;
  margin: 0 24px;
  flex: 0 0 ${FLOWCODE_IMAGE_SIZE_PX}px;
  & > img {
    max-height: ${FLOWCODE_IMAGE_SIZE_PX}px;
    aspect-ratio: 1;
  }
`

const addOptionalToTitle = (text: string, addOptional: boolean): string =>
  `${text}${addOptional ? ' (optional)' : ''}`

const AddLinkStep: React.FC<{ stepNumber: number }> = ({ stepNumber }) => {
  return (
    <StepContainer
      number={stepNumber}
      title="Choose your destination, then enter a link or file"
      render={titleId => (
        <InputWrapper aria-labelledby={titleId} role="group">
          <SimplifiedScanDestinationV1
            variant={2}
            destinations={SIMPLIFIED_DESKTOP_SCAN_DESTINATIONS}
            onChange={destination => {
              events.Flowcode_Simplified_Desktop_Homepage_Selected_Destination({
                destination: destination.id,
                variant: 2
              })
            }}
            onInputChange={destination => {
              events.Flowcode_Simplified_Desktop_Homepage_Entered_Destination({
                destination: destination.id,
                variant: 2
              })
            }}
          />
        </InputWrapper>
      )}
    />
  )
}

const ChooseColorStep: React.FC<{
  stepNumber: number
  addOptional: boolean
  flowcodeColor: FlowcodeColorOptionType
}> = ({ stepNumber, addOptional, flowcodeColor }) => {
  const { setColor } = useCreateFlowcodeStateActions()
  const { t } = useTranslation('codes-pages')
  const [colorOptionsVariant] = useFlowExperiment(REDUCE_COLORS_FC_DESKTOP_GENERATOR)
  const colorOptions =
    colorOptionsVariant === 'variation_1'
      ? UPDATED_FLOWCODE_COLOR_OPTIONS
      : LOGGED_OUT_GENERATOR_COLORS
  const defaultColor = 'black'

  return (
    <StepContainer
      number={stepNumber}
      title={addOptionalToTitle(t('CodesPagesDesign.SelectColor'), addOptional)}
      render={titleId => (
        <InputWrapper>
          <ColorSelector
            aria-labelledby={titleId}
            defaultColorId={defaultColor}
            activeColor={flowcodeColor}
            setActiveColor={activeColor => {
              setColor(activeColor)
              events.Flowcode_Simplified_Desktop_Homepage_Selected_Color({
                color: activeColor.id,
                variant: 2
              })
            }}
            customColorSet={colorOptions}
          />
        </InputWrapper>
      )}
    />
  )
}

const LastStepOuterContainer: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    width="100%"
    backgroundColor="secondary.highlight"
    padding="0 42px 22px 42px"
    flexDirection="column"
    borderRadius="0 0 12px 12px"
  >
    <Box height="12px" ml="12px" borderLeft="1px solid black" width="100%" />
    {children}
  </Box>
)

export const SimplifiedDesktop: React.FC = () => {
  const { clearCenterImage, setCenterImage } = useAddCenterImageState()
  const handleCenterImageChange = useHandleCenterImageChange(setCenterImage)
  const { templates: flowcodeDesignOptions } = useFlowcodeTemplates()
  const { handleDesignOptionClick, handleShapeOptionClick } = useManualDesignStep()
  const {
    color: flowcodeColor,
    template: flowcodeDesign,
    centerImage: flowcodeLogo,
    borderShape,
    options: flowcodeOptions
  } = useCreateFlowcodeState()
  const { isAuthChecked, isAuthenticated, isViewer } = useAuthState()
  const isAccountPaused = useIsAccountPaused()
  const redirectToPausedAccountPage = useRedirectToPausedAccountPage()
  const [autoSubmitForm, setAutoSubmitForm] = useState<boolean>(false)
  const shouldShowSignUpForm = !isAuthChecked || !isAuthenticated
  const authStepTitle = shouldShowSignUpForm
    ? 'Sign up to edit, print, and create more codes'
    : 'Click to create your Flowcode'

  const { handleSubmit } = useCreateFlowcodeForm()
  const createCodeAndDownloadAndRedirect = useCreateAndDownloadCode()
  const authFormRef = useRef<HTMLFormElement>(null)
  const [open, setOpen] = useState(false)
  const [file, setFile] = React.useState<File | string | undefined>()
  const [loading, setLoading] = React.useState(false)

  const handleUploadImage = async (imageUrl: string): Promise<void> => {
    try {
      setLoading(true)
      const filename = file instanceof File ? file.name : 'flowcode-center-img'
      const loadedImage = await loadImage(imageUrl, filename)

      handleCenterImageChange({
        target: {
          files: [loadedImage]
        }
      } as unknown as React.ChangeEvent<HTMLInputElement>)
      setOpen(false)
    } catch (e) {
      console.error(e)
      sendErrorNotification('Error uploading image')
    } finally {
      setLoading(false)
    }
  }

  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false)
  const openUpgradeModal = useCallback(() => {
    setUpgradeModalOpen(true)
  }, [])

  const onUpgradeModalClose = useCallback<UpsellModalProps['onClose']>(
    async reason => {
      setUpgradeModalOpen(false)
      if (reason === 'success') {
        await createCodeAndDownloadAndRedirect(undefined, () => openUpgradeModal())
      }
    },
    [createCodeAndDownloadAndRedirect, openUpgradeModal]
  )

  useEffect(() => {
    // we have to wait jwt token to set up (function useListenForAuthChanges) and redux store update before we create a code
    // autoSubmitForm should only be true if returning from logging in
    if (autoSubmitForm && isAuthenticated) {
      if (isAccountPaused) redirectToPausedAccountPage()
      if (isViewer) {
        sendErrorNotification('View only members are not able to complete this action')
        router.push({
          pathname: Routes.CODES
        })
      }

      createCodeAndDownloadAndRedirect(undefined, () => {
        openUpgradeModal()
      })
      setAutoSubmitForm(false)
    }
  }, [
    isViewer,
    isAuthenticated,
    isAccountPaused,
    createCodeAndDownloadAndRedirect,
    setAutoSubmitForm,
    autoSubmitForm,
    redirectToPausedAccountPage,
    openUpgradeModal
  ])

  const onSubmit = async (): Promise<void> => {
    /* user should be authenticated to create a code, this form can be submitted 
       if a user hits enter while focused on an input field, triggering the auth form submit 
       will focus the final step which also helps in case it's below the fold*/
    if (shouldShowSignUpForm) {
      authFormRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }))
      return
    }
    await createCodeAndDownloadAndRedirect(undefined, () => openUpgradeModal())
  }

  return (
    <Box width="100%" data-testid="simplified-desktop-v2" flexDirection={'column'}>
      <FlowcodeLimitUpsellModal open={upgradeModalOpen} onClose={onUpgradeModalClose} />
      <Box
        boxShadow="3px 3px 30px 0px #1111111A"
        alignItems="center"
        margin="24px auto 48px auto"
        borderRadius="12px"
        position="relative"
        backgroundColor="primary.white"
        width="100%"
        flexDirection="column"
        data-testid={`simplified-desktop-homepage-2`}
        height="auto"
        maxWidth="1220px"
        display="block"
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box
            flexDirection="row"
            justifyContent="space-between"
            width="100%"
            padding="20px 0 0 42px"
          >
            <Box flexDirection="column" alignContent={'flex-start'} flex="1 1 auto">
              <ChooseColorStep stepNumber={1} addOptional={true} flowcodeColor={flowcodeColor} />
              <StepContainer
                number={2}
                title={addOptionalToTitle('Choose your code shape', true)}
                render={titleId => (
                  <InputWrapper>
                    <ShapeSelector
                      aria-labelledby={titleId}
                      selectedShape={borderShape}
                      handleSelectShape={handleShapeOptionClick}
                      customEvent={label => {
                        events.Flowcode_Simplified_Desktop_Homepage_Selected_Shape({
                          shape: label,
                          variant: 2
                        })
                      }}
                    />
                  </InputWrapper>
                )}
              />

              <StepContainer
                number={3}
                title={addOptionalToTitle('Choose a code pattern', true)}
                render={titleId => (
                  <InputWrapper>
                    <DesignOptions
                      aria-labelledby={titleId}
                      options={flowcodeDesignOptions}
                      selectedOption={flowcodeDesign?.id}
                      onClick={(pattern: FlowcodeTemplateType) => {
                        handleDesignOptionClick(pattern)
                        events.Flowcode_Simplified_Desktop_Homepage_Selected_Pattern({
                          pattern: pattern.id as string,
                          variant: 2
                        })
                      }}
                    />
                  </InputWrapper>
                )}
              />

              <AddLinkStep stepNumber={4} />
            </Box>
            <Box justifyContent="space-evenly" flex="0 0 auto">
              <HorizontalSpacer mr="10px" />
              <VerticalDivider
                border={`1px solid ${theme.colors.secondary.border}`}
                height="calc(100% - 30px)"
                margin="10px 0 20px 0 "
              />
              <HorizontalSpacer />
            </Box>
            <FlowcodeBox>
              <Flowcode options={flowcodeOptions} />
            </FlowcodeBox>
          </Box>

          {/* We want this step to be inside the form when signed in, as it's the form's submit button */}
          {!shouldShowSignUpForm && (
            <LastStepOuterContainer>
              <StepContainer
                number={5}
                title={authStepTitle}
                render={titleId => (
                  <Box role="group" aria-labelledby={titleId}>
                    <LoggedInDownloadStep aria-labelledby={titleId} />
                  </Box>
                )}
              />
            </LastStepOuterContainer>
          )}
        </form>

        {/* <ImageEditor
                open={open}
                handleUploadImage={handleUploadImage}
                onClose={() => {
                  setOpen(false)
                }}
                useObjectURL
                file={file}
                hideMask={true}
                maskType={MaskType.RECTANGLE}
                title="Upload an Image"
                handleDeleteImage={() => {
                  clearCenterImage()
                  setOpen(false)
                  setFile('')
                }}
              /> */}

        {/* We want this step to be outside the form when signed out, as it will render its own sign up form, and we can't have nested forms */}
        {shouldShowSignUpForm && (
          <LastStepOuterContainer>
            <StepContainer
              number={5}
              title={authStepTitle}
              render={titleId => (
                <Box role="group" aria-labelledby={titleId}>
                  <LoggedOutDownloadStep
                    formRef={authFormRef}
                    aria-labelledby={titleId}
                    onSuccessLogIn={() => {
                      setAutoSubmitForm(true)
                    }}
                  />
                </Box>
              )}
            />
          </LastStepOuterContainer>
        )}
      </Box>
    </Box>
  )
}
